import { faUserCircle } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, Button, CircularProgress, Divider, Fade, IconButton, Link, Menu, MenuItem, Toolbar, Tooltip, Typography, styled, useTheme } from '@mui/material';
import { FC, MouseEvent, useCallback, useContext, useState } from 'react';
import { Outlet, Link as RouteLink } from 'react-router-dom';

import BctLogoLight from '@/assets/images/logo-top-trim-icon-reposition.png';

import Image from '@/components/layout/Image';
import ToggleFeedbackButton from '@/components/common/ToggleFeedbackButton';
import { AppSettings } from '@/appsettings';
import AuthContext from '@/context/AuthContext';
import ErrorContext from '@/context/ErrorContext';
import HidingContext from '@/context/HideContext';
import LoadingContext from '@/context/LoadingContext';
import ErrorPage from '@/pages/ErrorPage';
import IHasChildren from '@/types/IHasChildren';
import OverlayLoading from '../common/overlayLoading/OverlayLoading';


interface IBaseLayoutProps extends IHasChildren {}
const Center = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px'
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    background: theme.palette.common.white,
}));

const StyledTitle = styled(Typography)(({ theme}) => ({
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        theme.palette.primary.main,
    textDecoration: 'none',
    textTransform: 'uppercase',

}))

const StyledTitleLink = styled(Link)(({theme}) => ({
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0),
    textDecoration: 'none',
    padding: '5px 25px',
    whiteSpace: 'nowrap',
    //flex: '1 1 auto'
}));

const LogoutContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
}));

const StyledToolbar = styled(Toolbar)(({ theme}) => ({
    backgroundColor: theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,
    gap: '112px',
    minHeight: '56px !important',
    alignItems: 'stretch',
    justifyContent: 'space-between'
}));

const BaseLayout: FC<IBaseLayoutProps> = ({
    children
}) => {

    const { loading } = useContext(LoadingContext);
    const { hiding } = useContext(HidingContext);
    const { errors } = useContext(ErrorContext);



    
    const user = useContext(AuthContext);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box 
            sx={{
                display: 'flex',
                width: '100%',
            }}
        >
            {/* -- App Bar -- */}
            {
                !hiding ?
                <StyledAppBar position='fixed'>
                    <StyledToolbar>
                        {/* -- LOGO -- */}
                        <Box
                            sx={{
                                alignSelf: 'center',
                                lineHeight: 0
                            }}
                            component={RouteLink}
                            to='/'
                        >
                        <Image
                            sx={{
                                height: 64,
                                display:{ xs: 'none', md: 'block'}
                            }}
                            alt="BCT Logo"
                            src={BctLogoLight}
                        />
                        </Box>
                        {/* -- HEADER TITLE -- */}
                        {/* need to move this all the way to the left */}
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: -15, mr: 'auto' }}>
                            <StyledTitleLink >
                                <StyledTitle sx={{ alignSelf: 'center' }} fontWeight={500} variant="h5" display={{ xs: 'none', md: 'block' }}>
                                    Redact
                                </StyledTitle>
                            </StyledTitleLink>
                        </Box>

                        {/* -- HOME BUTTONS -- */}
                        <LogoutContainer>
                            {/* -- USER MANUAL -- */}
                            <Button
                                variant="outlined"
                                color="secondary"
                                href={`/get/user-manual`}
                                target='_blank'
                                component="a"
                                size='small'
                                sx={{ marginRight: 2 }}
                            >
                                Redact Function User Manual
                            </Button>

                            {/* -- REPORT A BUG -- */}
                            {
                                AppSettings.sentry.dsn &&
                                AppSettings.sentry.enableReportABug && (
                                    <ToggleFeedbackButton />
                                )
                            }

                            {/* -- USER PROFILE -- */}
                            {
                                !!user && 
                                <>
                                    <IconButton
                                        id="btn-auth"
                                        aria-label="account of current user"
                                        aria-controls="menu-auth"
                                        aria-haspopup="true"
                                        onClick={handleMenu}
                                        color="inherit">
                                        <FontAwesomeIcon icon={faUserCircle} />
                                    </IconButton>
                                    <Menu
                                        id="menu-auth"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        sx={{ mt: 4 }}
                                        transformOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}

                                        open={anchorEl?.id === 'btn-auth'}
                                        onClose={handleClose}
                                    >
                                        <MenuItem>{user.data?.displayName ?? user.data?.userName}</MenuItem>
                                        <Divider />
                                        {/* {isAdmin && <MenuItem component={RouteLink} to='/administration' onClick={handleClose}>Administration</MenuItem>} */}
                                        {/* <MenuItem component={RouteLink} to="/account/password" onClick={handleClose}>Change Password</MenuItem> */}
                                        <MenuItem component={"a"} href="/auth/logout">Logout</MenuItem>
                                    </Menu>
                                </>
                            }
                        </LogoutContainer>
                    </StyledToolbar>
                </StyledAppBar> :
                null
            }
            {/* -- Body Content -- */}
            {
                errors.length > 0 ?
                    <ErrorPage error={errors[0]} /> :
                !user.data ? 
                    <Center width="100%">
                        <OverlayLoading/> 
                    </Center> :
                    <Box 
                        width="100%" 
                        height="100%" 
                        bgcolor='#F6F8FA'
                    >
                        <Fade appear in={!loading}>
                            <Box width="100%" height="100%">
                                <Outlet />
                                {children}
                            </Box>
                        </Fade>
                    </Box>
            }
        </Box>
    )
}

export default BaseLayout